import { z } from 'zod';
import { DocumentTypeSchema } from '../catalogs/domain-catalogs.js';
import { BusinessDateSchema, TimestampSchema } from '../common/clock.js';
import { CreditLineIdSchema, CreditPaymentIdSchema, CustomerIdSchema, DrawdownIdSchema, } from '../common/ids.js';
import { MoneySchema, PositiveMoneySchema } from '../common/money.js';
import { RateSchema } from '../common/rate.js';
import { PrepaymentModeSchema } from './product.js';
import { CreditAuthorizationSchema, CreditLineSummarySchema, DrawdownSchema, MerchantRefSchema, PaymentAllocationSchema, } from './schemas.js';
import { BlockReasonSchema } from './states.js';
/**
 * API REST de Paguz Credit Core (`/cc/v1`, `05-core-credito.md` §10): cuerpos de petición y respuestas
 * propias del core. La consume `PaguzCreditCoreAdapter` (`CreditCorePort`) y la consola.
 * Autenticación servicio-a-servicio (client credentials, scope `credit-core`); `Idempotency-Key`
 * obligatoria en los `POST` que mueven saldos.
 */
// ── Autenticación (client credentials) ───────────────────────────────────
export const CcTokenRequestSchema = z
    .object({
    grant_type: z.literal('client_credentials'),
    client_id: z.string().min(1),
    client_secret: z.string().min(1),
    scope: z.string().optional(),
})
    .meta({ id: 'CcTokenRequest' });
export const CcTokenResponseSchema = z
    .object({
    access_token: z.string(),
    token_type: z.literal('Bearer'),
    expires_in: z.int(),
    scope: z.string(),
})
    .meta({ id: 'CcTokenResponse' });
// ── Clientes y líneas ─────────────────────────────────────────────────────
export const CcCustomerInputSchema = z.object({
    externalCustomerId: z.string().min(1).max(100),
    documentType: DocumentTypeSchema.optional(),
    documentNumber: z.string().min(3).max(20).optional(),
    fullName: z.string().min(1).max(200),
});
export const CcCreateCustomerRequestSchema = CcCustomerInputSchema.meta({
    id: 'CcCreateCustomerRequest',
});
export const CcCustomerSchema = z
    .object({
    id: CustomerIdSchema,
    externalCustomerId: z.string(),
    documentType: DocumentTypeSchema.nullable(),
    documentNumber: z.string().nullable(),
    fullName: z.string(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'CcCustomer' });
export const CcOpenLineRequestSchema = z
    .object({
    /** Cliente existente (`cus_…`) o datos para crearlo/encontrarlo por `externalCustomerId`. */
    customerId: CustomerIdSchema.optional(),
    customer: CcCustomerInputSchema.optional(),
    productCode: z.string().min(1),
    approvedLimit: PositiveMoneySchema,
    /** `app_…` del expediente (idempotencia natural de la apertura). */
    externalRef: z.string().min(1).max(100),
    /** Abre directamente en `ACTIVE` (seed/demo); por defecto `PENDING_ACTIVATION`. */
    activate: z.boolean().default(false),
})
    .refine((v) => v.customerId !== undefined || v.customer !== undefined, {
    message: 'Se requiere customerId o customer',
})
    .meta({ id: 'CcOpenLineRequest' });
export const CcLineStatusActionSchema = z.enum(['ACTIVATE', 'BLOCK', 'UNBLOCK', 'CLOSE']);
export const CcLineStatusRequestSchema = z
    .object({
    action: CcLineStatusActionSchema,
    reason: BlockReasonSchema.optional(),
    actor: z.enum(['CUSTOMER', 'ANALYST', 'SYSTEM']),
})
    .meta({ id: 'CcLineStatusRequest' });
export const CcUpdateLimitRequestSchema = z
    .object({ newLimit: PositiveMoneySchema, reason: z.string().min(1).max(200) })
    .meta({ id: 'CcUpdateLimitRequest' });
export const CcLineLookupQuerySchema = z.object({
    externalCustomerId: z.string().optional(),
    documentType: DocumentTypeSchema.optional(),
    documentNumber: z.string().optional(),
});
// ── Simulación ────────────────────────────────────────────────────────────
export const CcSimulationRequestSchema = z
    .object({
    /** Simula con el producto de la línea o con un producto (sin línea). */
    lineId: CreditLineIdSchema.optional(),
    productCode: z.string().optional(),
    amount: PositiveMoneySchema,
    installments: z.int().min(1).max(120),
})
    .refine((v) => v.lineId !== undefined || v.productCode !== undefined, {
    message: 'Se requiere lineId o productCode',
})
    .meta({ id: 'CcSimulationRequest' });
// ── Autorizaciones ────────────────────────────────────────────────────────
export const CcAuthorizeRequestSchema = z
    .object({
    amount: PositiveMoneySchema,
    installments: z.int().min(1).max(120),
    merchant: MerchantRefSchema,
    /** `pi_…` de la intención de pago. */
    externalRef: z.string().min(1).max(100),
})
    .meta({ id: 'CcAuthorizeRequest' });
export const CcReleaseRequestSchema = z
    .object({
    reason: z.enum(['SAGA_COMPENSATION', 'CUSTOMER_CANCELLED', 'EXPIRED', 'OPERATOR']),
})
    .meta({ id: 'CcReleaseRequest' });
export const CcExtendRequestSchema = z
    .object({
    minutes: z
        .int()
        .min(1)
        .max(24 * 60),
})
    .meta({ id: 'CcExtendRequest' });
export const CcCaptureResultSchema = z
    .object({ authorization: CreditAuthorizationSchema, drawdown: DrawdownSchema })
    .meta({ id: 'CcCaptureResult' });
// ── Disputas, reversiones y devoluciones ──────────────────────────────────
export const CcDisputeRequestSchema = z
    .object({ disputeRef: z.string().min(1).max(100) })
    .meta({ id: 'CcDisputeRequest' });
export const CcResolveDisputeRequestSchema = z
    .object({ disputeRef: z.string().min(1).max(100), outcome: z.enum(['REJECTED', 'ACCEPTED']) })
    .meta({ id: 'CcResolveDisputeRequest' });
export const CcReverseRequestSchema = z
    .object({
    /** Omitido = reversión total. */
    amount: PositiveMoneySchema.optional(),
    reason: z.enum(['DISPUTE_ACCEPTED', 'OPERATOR', 'MERCHANT_REFUND']),
    externalRef: z.string().min(1).max(100),
})
    .meta({ id: 'CcReverseRequest' });
export const CcReversalResultSchema = z
    .object({
    drawdown: DrawdownSchema,
    reversedPrincipal: MoneySchema,
    reversedInterest: MoneySchema,
    reversedFees: MoneySchema,
    reversedInsurance: MoneySchema,
    creditBalanceAdded: MoneySchema,
    availableLimitAfter: MoneySchema,
})
    .meta({ id: 'CcReversalResult' });
export const CcRefundRequestSchema = z
    .object({ amount: PositiveMoneySchema, externalRef: z.string().min(1).max(100) })
    .meta({ id: 'CcRefundRequest' });
export const CcRefundApplicationSchema = z
    .object({
    drawdown: DrawdownSchema,
    allocation: PaymentAllocationSchema,
    creditBalanceAdded: MoneySchema,
})
    .meta({ id: 'CcRefundApplication' });
// ── Pagos ─────────────────────────────────────────────────────────────────
export const CcPaymentRequestSchema = z
    .object({
    amount: PositiveMoneySchema,
    channel: z.enum(['BREB', 'DEPOSIT_ACCOUNT', 'CASH', 'ADJUSTMENT']),
    externalRef: z.string().min(1).max(100),
    /** Abono extraordinario: el excedente va a capital (por defecto la utilización más antigua). */
    prepayment: z
        .object({ drawdownId: DrawdownIdSchema.optional(), mode: PrepaymentModeSchema })
        .optional(),
})
    .meta({ id: 'CcPaymentRequest' });
export const CcRegisteredPaymentSchema = z
    .object({
    paymentId: CreditPaymentIdSchema,
    allocation: PaymentAllocationSchema,
    line: CreditLineSummarySchema,
    receivedAt: TimestampSchema,
})
    .meta({ id: 'CcRegisteredPayment' });
// ── Contabilidad ──────────────────────────────────────────────────────────
export const CcJournalEntrySchema = z
    .object({
    id: z.string(),
    businessDate: BusinessDateSchema,
    eventType: z.string(),
    description: z.string(),
    lines: z.array(z.object({
        account: z.string(),
        debit: z.string(),
        credit: z.string(),
        drawdownId: z.string().nullable(),
    })),
})
    .meta({ id: 'CcJournalEntry' });
// ── Reloj y EOD (demo / operación) ────────────────────────────────────────
export const CcClockSchema = z
    .object({
    now: TimestampSchema,
    businessDate: BusinessDateSchema,
    mode: z.enum(['REAL', 'SIMULATED']),
    offsetDays: z.int(),
    frozen: z.boolean(),
})
    .meta({ id: 'CcClock' });
export const CcAdvanceClockRequestSchema = z
    .object({ days: z.int().min(1).max(400) })
    .meta({ id: 'CcAdvanceClockRequest' });
export const CcEodStepSchema = z.enum([
    'expire-holds',
    'accrue-interest',
    'age-installments',
    'cut-cycles',
    'ledger-invariants',
]);
export const CcEodRunRequestSchema = z
    .object({ businessDate: BusinessDateSchema.optional() })
    .meta({ id: 'CcEodRunRequest' });
export const CcEodRunResultSchema = z
    .object({
    businessDate: BusinessDateSchema,
    steps: z.array(z.object({
        step: CcEodStepSchema,
        status: z.enum(['DONE', 'SKIPPED']),
        stats: z.record(z.string(), z.union([z.number(), z.string()])),
    })),
})
    .meta({ id: 'CcEodRunResult' });
export const CcAdvanceClockResultSchema = z
    .object({ clock: CcClockSchema, eod: z.array(CcEodRunResultSchema) })
    .meta({ id: 'CcAdvanceClockResult' });
/** Respuesta de `GET/POST /usury-rates`. */
export const CcUsuryRateListSchema = z.object({
    data: z.array(z.object({
        modality: z.string(),
        validFrom: BusinessDateSchema,
        validTo: BusinessDateSchema,
        rateEA: RateSchema,
    })),
});
//# sourceMappingURL=core-api.js.map